// Yup schemas for user/login endpoints.
//
// Each schema mirrors the BE Pydantic Request* model 1:1 — same field names,
// same required/optional, same constraints. When the BE schema changes, update
// the matching schema here. Codegen from OpenAPI can later replace this file.
//
// Naming: <verb><Entity>Schema, e.g. createStaffLoginSchema, updateCustomerSchema.
// Each schema's JSDoc lists the endpoint it validates against.

import * as yup from "yup";
import { PERSONA_TYPE, CUSTOMER_TYPE } from "../../constants/enums";

const E164_OR_FREEFORM_PHONE = /^\+?[0-9][0-9\s\-()]{6,}$/;

/**
 * POST /login — create a staff or BV-associate login. The BE generates the
 * password and emails it to `email`. Used by RoleManagement → Add User Role.
 * Mirrors LoginRequest (services/shared/shared_layer/schemas/login.py).
 */
export const createStaffLoginSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required("Full name is required")
    .min(2, "Full name must be at least 2 characters"),
  username: yup
    .string()
    .trim()
    .required("Username is required")
    .min(2, "Username must be at least 2 characters"),
  email: yup
    .string()
    .trim()
    .required("Email is required")
    .email("Enter a valid email — login credentials are sent there"),
  mobileNoWa: yup
    .string()
    .trim()
    .required("WhatsApp number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  personaType: yup
    .string()
    .oneOf(
      [PERSONA_TYPE.STAFF, PERSONA_TYPE.BV_ASSOCIATE],
      "User type must be Staff or BV Associate",
    )
    .required("User type is required"),
});

/**
 * PUT /login?loginId={id} — edit an existing login. Same shape as create,
 * but persona is immutable post-creation so it's omitted from validation
 * (the FE disables the field; if it sneaks in, BE ignores it).
 */
export const updateStaffLoginSchema = createStaffLoginSchema.omit(["personaType"]);

/**
 * POST /customers — create a developer/agent/zumale customer account.
 * Mirrors RequestCustomerAccountData. Used by ZumaleUser CustomerAddEditModal
 * and (after migration) the customers dashboard. Phones are optional on the
 * BE; we mark them required here because all consumers of this schema need
 * a contact method.
 */
export const createCustomerSchema = yup.object({
  companyName: yup.string().trim().required("Company name is required"),
  ownerName: yup.string().trim().required("Owner name is required"),
  emailId: yup.string().trim().required("Email is required").email("Enter a valid email"),
  mobileNoWa: yup
    .string()
    .trim()
    .required("WhatsApp number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  mobileNoCalling: yup
    .string()
    .trim()
    .nullable()
    .test("phone-or-empty", "Enter a valid phone number", (v) =>
      !v || E164_OR_FREEFORM_PHONE.test(v),
    ),
  customerType: yup
    .string()
    .oneOf(Object.values(CUSTOMER_TYPE), "Invalid customer type")
    .required("Customer type is required"),
  isActive: yup.boolean().default(true),
  idDeveloperApprovalRequired: yup.boolean().default(false),
  // `Field type="date"` (CustomDatePicker) stores a real Date object in form
  // state. Without a transform, `yup.string()` casts via `String(date)` which
  // emits the JS toString form ("Sun Jan 01 2006 00:00:00 GMT+0530 (India
  // Standard Time)") — pydantic's date parser on the BE rejects that. Force
  // ISO YYYY-MM-DD here so the wire format is always BE-friendly. Strings
  // already in ISO form pass through unchanged.
  dob: yup
    .mixed()
    .nullable()
    .transform((value) => {
      if (value == null || value === "") return null;
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().split("T")[0];
      }
      return value;
    }),
  address: yup
    .object({
      buildingNo: yup.string().trim().nullable(),
      streetName1: yup.string().trim().nullable(),
      streetName2: yup.string().trim().nullable(),
      city: yup.string().trim().nullable(),
      district: yup.string().trim().nullable(),
      state: yup.string().trim().nullable(),
      pinCode: yup.string().trim().nullable(),
      country: yup.string().trim().nullable(),
    })
    .default({}),
});

/**
 * Channel-partner (agent) profile. Captured via the AgentList add/edit modal
 * and shipped as multipart FormData (legacy BE expectation) — the validation
 * runs on the plain object before the FormData is built.
 *
 * Notably stricter than createCustomerSchema:
 *   - mobileNoCalling REQUIRED (not optional)
 *   - reraNumber REQUIRED
 *   - all address sub-fields REQUIRED
 *   - pinCode constrained to 6 digits
 */
export const agentSchema = yup.object({
  companyName: yup
    .string()
    .trim()
    .required("Company name is required")
    .max(100, "Company name cannot exceed 100 characters"),
  ownerName: yup
    .string()
    .trim()
    .required("Owner name is required")
    .max(100, "Owner name cannot exceed 100 characters"),
  emailId: yup.string().trim().required("Email is required").email("Enter a valid email"),
  mobileNoWa: yup
    .string()
    .trim()
    .required("WhatsApp number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  mobileNoCalling: yup
    .string()
    .trim()
    .required("Calling number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  reraNumber: yup.string().trim().required("RERA number is required"),
  customerType: yup.string().default("AGENT"),
  isActive: yup.boolean().default(true),
  metaId: yup.string().nullable(),
  whatsappSecretAccessToken: yup.string().nullable(),
  emailPasswordSecret: yup.string().nullable(),
  address: yup.object({
    buildingNo: yup.string().trim().required("Building number is required"),
    streetName1: yup.string().trim().required("Street name 1 is required"),
    streetName2: yup.string().trim().nullable(),
    city: yup.string().trim().required("City is required"),
    district: yup.string().trim().required("District is required"),
    state: yup.string().trim().required("State is required"),
    pinCode: yup
      .string()
      .trim()
      .required("Pin code is required")
      .matches(/^\d{6}$/, "Pin code must be 6 digits"),
    country: yup.string().trim().required("Country is required"),
  }),
});

/**
 * Property-buyer user shape. Used by EditUserModal and related screens.
 * Address fields are optional individually; the BE accepts a partial address.
 * Mobile numbers are stored without the leading "+" in some callers (legacy),
 * so we allow either E.164 or freeform digits and let BE normalise.
 */
export const editUserSchema = yup.object({
  firstName: yup.string().trim().required("First name is required"),
  lastName: yup.string().trim().required("Last name is required"),
  emailId: yup.string().trim().required("Email is required").email("Enter a valid email"),
  mobileNoWa: yup
    .string()
    .trim()
    .required("WhatsApp number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  mobileNoCalling: yup
    .string()
    .trim()
    .required("Calling number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  gender: yup.string().oneOf(["MALE", "FEMALE", ""], "Invalid gender").nullable(),
  dob: yup.string().nullable(),
  address: yup
    .object({
      buildingNo: yup.string().trim().nullable(),
      streetName1: yup.string().trim().nullable(),
      streetName2: yup.string().trim().nullable(),
      city: yup.string().trim().required("City is required"),
      district: yup.string().trim().required("District is required"),
      state: yup.string().trim().required("State is required"),
      pinCode: yup
        .string()
        .trim()
        .required("Pin code is required")
        .matches(/^\d{4,10}$/, "Pin code must be 4–10 digits"),
      country: yup.string().trim().nullable(),
    })
    .default({}),
});

/**
 * Secondary user (co-applicant) attached to an existing customer sale.
 * Same shape as editUserSchema but adds `title` + `occupation` since those
 * are required by the BE for the user creation flow.
 */
export const secondaryUserSchema = yup.object({
  title: yup.string().trim().oneOf(["MR", "MRS", "MS"], "Invalid title").required("Title is required"),
  firstName: yup.string().trim().required("First name is required"),
  lastName: yup.string().trim().required("Last name is required"),
  gender: yup.string().trim().oneOf(["MALE", "FEMALE", "OTHER"], "Invalid gender").required("Gender is required"),
  occupation: yup.string().trim().required("Occupation is required"),
  dob: yup.string().trim().required("Date of birth is required"),
  emailId: yup.string().trim().required("Email is required").email("Enter a valid email"),
  mobileNoWa: yup
    .string()
    .trim()
    .required("WhatsApp number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  mobileNoCalling: yup
    .string()
    .trim()
    .required("Calling number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid phone number"),
  address: yup
    .object({
      buildingNo: yup.string().trim().nullable(),
      streetName1: yup.string().trim().required("Street is required"),
      streetName2: yup.string().trim().nullable(),
      city: yup.string().trim().required("City is required"),
      district: yup.string().trim().required("District is required"),
      state: yup.string().trim().required("State is required"),
      pinCode: yup
        .string()
        .trim()
        .required("Pin code is required")
        .matches(/^\d{4,10}$/, "Pin code must be 4–10 digits"),
      country: yup.string().trim().nullable(),
    })
    .default({}),
});

export const SECONDARY_USER_TITLE_OPTIONS = [
  { value: "MR", label: "Mr." },
  { value: "MRS", label: "Mrs." },
  { value: "MS", label: "Ms." },
];

export const SECONDARY_USER_GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
];

export const SECONDARY_USER_OCCUPATION_OPTIONS = [
  "Engineering",
  "Agriculture",
  "Healthcare",
  "Education",
  "Business",
  "Finance",
  "Technology",
  "Arts",
  "Other",
].map((o) => ({ value: o, label: o }));
